<!--
  Detalhes de uma troca em transição (PENDING): página somente leitura, sem ações (CA4).
  Blocos: faixa-resumo (quem passou · status · respostas), convidados e o convite completo.
  O layout do app já fornece fundo branco e o padding da página (`container-dashboard`).
-->
<template>
  <div class="flex flex-col gap-6" data-testid="handover-details-page">
    <!-- Troca fora de transição: mantém o skeleton enquanto o guard redireciona. -->
    <template v-if="isLoadingEvaluation || isRedirecting">
      <div class="h-[120px] bg-gray-100 rounded-[12px] animate-pulse" />
      <div class="h-[220px] bg-gray-100 rounded-[12px] animate-pulse" />
      <div class="h-[420px] bg-gray-100 rounded-[12px] animate-pulse" />
    </template>

    <template v-else-if="packageData">
      <!-- 1. Faixa-resumo -->
      <div
        class="flex flex-col lg:flex-row lg:items-center gap-5 bg-white rounded-[12px] border-[1.5px] border-primary30 p-[20px]"
        data-testid="handover-details-summary"
      >
        <div class="flex flex-col gap-2 min-w-0">
          <span class="text-sm font-semibold text-[#7f7f8e] text-left">
            Profissional que passou
          </span>
          <DoctorInfo
            :name="packageData.requester.name"
            :subtitle="`CRM ${packageData.requester.crm}`"
            :avatar-url="packageData.requester.avatarUrl"
            :nps-score="packageData.requester.npsScore"
            size="md"
          />
        </div>

        <div class="hidden lg:block w-px self-stretch bg-[#e8e8ed] lg:ml-auto" />

        <div class="flex flex-col gap-2 lg:w-[200px]">
          <span class="text-sm font-semibold text-[#7f7f8e] text-left">Status</span>
          <div class="flex">
            <HandoverStatusBadge :status="packageData.status" />
          </div>
        </div>

        <div class="hidden lg:block w-px self-stretch bg-[#e8e8ed]" />

        <div class="flex flex-col gap-2 lg:w-[260px]">
          <span class="text-sm font-semibold text-[#7f7f8e] text-left">Respostas</span>
          <template v-if="inviteCount > 0">
            <span
              class="text-sm text-[#16161d] text-left"
              data-testid="handover-details-accepted-count"
            >
              <b>{{ acceptedCount }} de {{ inviteCount }}</b> aceitaram
            </span>
            <div
              class="h-1.5 w-full overflow-hidden rounded-full bg-[#e2e2e9]"
              role="progressbar"
              aria-label="Convidados que aceitaram"
              :aria-valuenow="acceptedCount"
              aria-valuemin="0"
              :aria-valuemax="inviteCount"
              data-testid="handover-details-progress"
            >
              <div
                class="h-full rounded-full bg-[#198038]"
                :style="{ width: `${acceptedPercent}%` }"
              />
            </div>
          </template>
          <span v-else class="text-sm text-[#7f7f8e] text-left">—</span>
        </div>
      </div>

      <!-- 2. Convidados -->
      <div class="flex flex-col bg-white rounded-[12px] border border-[#d8d8e2]">
        <div
          class="flex items-center justify-between px-[24px] py-[16px] bg-[#f8f8fa] border-b border-[#d8d8e2] rounded-t-[12px]"
        >
          <span class="text-base font-bold text-[#16161d] text-left">Convidados</span>
        </div>
        <div class="px-[24px] py-[20px]">
          <CandidatesList :invites="packageData.invites" :columns="2" />
        </div>
      </div>

      <!--
        3. Convite completo: PackageDetailCard sem alteração (também usado na /analisar).
        O raio de 12px é aplicado de fora, só nesta página: o card é compartilhado com a
        /analisar e o Vuetify força `.rounded-xl` (24px, !important). O seletor depende da
        estrutura interna do PackageDetailCard (raiz `div` + header como primeiro filho);
        se ela mudar, revisar estas classes.
      -->
      <div
        class="[&>div]:!rounded-[12px] [&>div>div:first-child]:!rounded-t-[12px] [&>div>div:first-child]:!rounded-b-none"
      >
        <PackageDetailCard :package-data="packageData" />
      </div>
    </template>

    <div
      v-else
      class="flex items-center justify-center py-[64px] text-sm text-[#7f7f8e]"
      data-testid="handover-details-not-found"
    >
      Nenhuma troca encontrada
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import DoctorInfo from '@/components/DoctorInfo.vue'
import { useHandoverStore } from '@/pinia-store/useHandoverStore'
import { useHeaderStore } from '@/pinia-store/useHeaderStore'
import { PackageStatus } from '@/Network/Types/Requests/HandoverInviteRequests'
import {
  approvalsQueueBreadcrumbs,
  approvalsQueueLocation
} from '@/modules/company/Approvals/Hub/queues'
import CandidatesList from './components/CandidatesList.vue'
import HandoverStatusBadge from './components/HandoverStatusBadge.vue'
import PackageDetailCard from './components/PackageDetailCard.vue'
import { getAcceptedCount, getAcceptedPercent, getInviteCount } from './handoverProgress'

const route = useRoute()
const router = useRouter()
const handoverStore = useHandoverStore()
const headerStore = useHeaderStore()

// Mesmo slot da avaliação: as duas páginas nunca ficam montadas ao mesmo tempo.
const { currentEvaluation, isLoadingEvaluation } = storeToRefs(handoverStore)

const packageData = computed(() => currentEvaluation.value?.package ?? null)

// Qualquer status diferente de PENDING sai da página (guard no onMounted).
const isRedirecting = computed(
  () => !!packageData.value?.status && packageData.value.status !== PackageStatus.PENDING
)

const inviteCount = computed(() => (packageData.value ? getInviteCount(packageData.value) : 0))
const acceptedCount = computed(() => (packageData.value ? getAcceptedCount(packageData.value) : 0))
const acceptedPercent = computed(() =>
  packageData.value ? getAcceptedPercent(packageData.value) : 0
)

// Setada no onUnmounted: a resposta da busca pode chegar depois que o usuário saiu.
let isUnmounted = false

onMounted(async () => {
  headerStore.setTitle(
    'Detalhes da troca',
    'Acompanhe quem foi convidado e quais plantões estão sendo passados',
    approvalsQueueBreadcrumbs('handoverApprovals')
  )

  const packageId = Number(route.params.packageId)

  // Id inválido (ex.: /aprovacoes/trocas/abc/detalhes): não dispara request com NaN.
  if (!Number.isInteger(packageId) || packageId <= 0) {
    handoverStore.setCurrentEvaluation(null)
    return
  }

  // Reaproveita o item que a tabela já carregou; outro pacote no store nunca é exibido.
  if (currentEvaluation.value?.package.id !== packageId) {
    await handoverStore.fetchAdminQueueItem(packageId)
  }

  // Usuário saiu da página durante a busca: não sequestra a navegação dele.
  if (isUnmounted) return

  // Fica na página quando não há o que redirecionar: erro/404 (currentEvaluation = null →
  // "Nenhuma troca encontrada") ou troca PENDING (em transição → renderiza os detalhes).
  if (!isRedirecting.value) return
  if (packageData.value?.status === PackageStatus.IN_ADMIN_QUEUE) {
    router.replace({ name: 'HandoverEvaluation', params: { packageId: String(packageId) } })
  } else {
    router.replace(approvalsQueueLocation('handoverApprovals'))
  }
})

onUnmounted(() => {
  isUnmounted = true
  handoverStore.setCurrentEvaluation(null)
})
</script>
